import React, { useState, useEffect } from "react";
import {
  FaFileAlt,
  FaCalendarAlt,
  FaRupeeSign,
  FaUsers,
  FaSync,
  FaExclamationTriangle,
  FaClock,
  FaTrophy,
} from "react-icons/fa";
import {
  CONTACTUS_API,
  get_donations,
  get_members,
  upcoming_events,
} from "../../hooks/Apis";

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dashboardData, setDashboardData] = useState({
    totalFormSubmissions: 0,
    todayFormSubmissions: 0,
    upcomingEventsCount: 0,
    totalDonations: 0,
    todayDonations: 0,
    totalMembers: 0,
    membershipTypes: {},
    recentSubmissions: [],
    recentDonations: [],
    recentMembers: [],
    upcomingEventsList: [],
  });

  const adminKey = process.env.REACT_APP_ADMIN_ACCESS_KEY;

  useEffect(() => {
    fetchAllDashboardData();
  }, []);

  const fetchAllDashboardData = async () => {
    try {
      setLoading(true);
      setError("");

      // Fetch all data in parallel
      const [
        contactsRes,
        donationsRes,
        membersRes,
        membersStatsRes,
        eventsRes,
      ] = await Promise.all([
        fetch(CONTACTUS_API),
        fetch(`${get_donations}?data=statistics`, {
          method: "GET",
          headers: {
            "X-Admin-Key": adminKey,
            "Content-Type": "application/json",
          },
        }),
        fetch(`${get_members}?data=members`, {
          method: "GET",
          headers: {
            "X-Admin-Key": adminKey,
            "Content-Type": "application/json",
          },
        }),
        fetch(`${get_members}?data=statistics`, {
          method: "GET",
          headers: {
            "X-Admin-Key": adminKey,
            "Content-Type": "application/json",
          },
        }),
        fetch(upcoming_events),
      ]);

      // Check response status for all API calls
      if (!contactsRes.ok)
        throw new Error(`Contacts API failed: ${contactsRes.status}`);
      if (!donationsRes.ok)
        throw new Error(`Donations API failed: ${donationsRes.status}`);
      if (!membersRes.ok)
        throw new Error(`Members API failed: ${membersRes.status}`);
      if (!membersStatsRes.ok)
        throw new Error(`Members Stats API failed: ${membersStatsRes.status}`);
      if (!eventsRes.ok)
        throw new Error(`Events API failed: ${eventsRes.status}`);

      const contactsData = await contactsRes.json();
      const donationsData = await donationsRes.json();
      const membersData = await membersRes.json();
      const membersStatsData = await membersStatsRes.json();
      const eventsData = await eventsRes.json();

      console.log("Members Data:", membersData);
      console.log("Members Stats Data:", membersStatsData);

      // Process contact submissions
      let totalFormSubmissions = 0;
      let todayFormSubmissions = 0;
      const today = new Date().toISOString().split("T")[0];

      if (contactsData.success && Array.isArray(contactsData.data)) {
        totalFormSubmissions = contactsData.data.length;
        todayFormSubmissions = contactsData.data.filter((item) => {
          const itemDate = item.created_at ? item.created_at.split("T")[0] : "";
          return itemDate === today;
        }).length;
      }

      // Process donations statistics
      let totalDonations = 0;
      let todayDonations = 0;
      if (donationsData.success && donationsData.data) {
        totalDonations = donationsData.data.total_donations || 0;
        todayDonations = donationsData.data.today_donations || 0;
      }

      // Process members - count from members list and get breakdown from stats
      let totalMembers = 0;
      let membershipTypes = {};

      // Try to get total from members list first
      if (membersData.success && Array.isArray(membersData.data)) {
        totalMembers = membersData.data.length;

        // Calculate membership breakdown
        const breakdown = {};
        membersData.data.forEach((member) => {
          const type = member.membership_type || "Unknown";
          breakdown[type] = (breakdown[type] || 0) + 1;
        });
        membershipTypes = breakdown;
      } else if (membersStatsData.success && membersStatsData.data) {
        // Fallback to stats data
        totalMembers = membersStatsData.data.total_members || 0;
        membershipTypes = membersStatsData.data.membership_breakdown || {};
      }

      // Process upcoming events
      let upcomingEventsList = [];
      let upcomingEventsCount = 0;
      if (eventsData.success && Array.isArray(eventsData.data)) {
        const now = new Date();
        upcomingEventsList = eventsData.data
          .filter((event) => {
            const eventDate = new Date(event.date);
            return eventDate > now;
          })
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 5);
        upcomingEventsCount = upcomingEventsList.length;
      }

      setDashboardData({
        totalFormSubmissions,
        todayFormSubmissions,
        upcomingEventsCount,
        totalDonations,
        todayDonations,
        totalMembers,
        membershipTypes,
        upcomingEventsList,
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({
    icon: Icon,
    title,
    value,
    subtitle,
    borderColor,
    iconBgColor,
  }) => (
    <div
      className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${borderColor}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold text-gray-800 mt-2">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${iconBgColor}`}>
          <Icon className="text-2xl text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <FaSync className="text-4xl text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Dashboard</h1>
          <p className="text-gray-600">Welcome to CRC Bharat Admin Panel</p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            <FaExclamationTriangle className="inline mr-2" />
            {error}
          </div>
        )}

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={FaFileAlt}
            title="Form Submissions"
            value={dashboardData.totalFormSubmissions}
            subtitle={`${dashboardData.todayFormSubmissions} today`}
            borderColor="border-blue-500"
            iconBgColor="bg-blue-500"
          />
          <StatCard
            icon={FaCalendarAlt}
            title="Upcoming Events"
            value={dashboardData.upcomingEventsCount}
            subtitle="Events scheduled"
            borderColor="border-purple-500"
            iconBgColor="bg-purple-500"
          />
          <StatCard
            icon={FaRupeeSign}
            title="Total Donations"
            value={`₹${dashboardData.totalDonations.toLocaleString("en-IN")}`}
            subtitle={`₹${dashboardData.todayDonations.toLocaleString(
              "en-IN"
            )} today`}
            borderColor="border-green-500"
            iconBgColor="bg-green-500"
          />
          <StatCard
            icon={FaUsers}
            title="Total Members"
            value={dashboardData.totalMembers}
            subtitle="Active members"
            borderColor="border-orange-500"
            iconBgColor="bg-orange-500"
          />
        </div>

        {/* Membership Types */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <FaClock className="mr-2 text-blue-600" />
              Members by Membership Type
            </h2>
            {Object.keys(dashboardData.membershipTypes).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(dashboardData.membershipTypes).map(
                  ([type, count], index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded"
                    >
                      <span className="text-gray-700 capitalize">{type}</span>
                      <span className="font-bold text-gray-800">{count}</span>
                    </div>
                  )
                )}
              </div>
            ) : (
              <p className="text-gray-500">No membership data available</p>
            )}
          </div>

          {/* Quick Summary */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <FaTrophy className="mr-2 text-yellow-600" />
              Quick Summary
            </h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded">
                <span className="text-gray-700">
                  📋 Today's Form Submissions
                </span>
                <span className="font-bold text-blue-600">
                  {dashboardData.todayFormSubmissions}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 rounded">
                <span className="text-gray-700">💰 Today's Donations</span>
                <span className="font-bold text-green-600">
                  ₹{dashboardData.todayDonations.toLocaleString("en-IN")}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded">
                <span className="text-gray-700">📅 Upcoming Events</span>
                <span className="font-bold text-purple-600">
                  {dashboardData.upcomingEventsCount}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded">
                <span className="text-gray-700">👥 Active Members</span>
                <span className="font-bold text-orange-600">
                  {dashboardData.totalMembers}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <FaCalendarAlt className="mr-2 text-purple-600" />
            Upcoming Events
          </h2>
          {dashboardData.upcomingEventsList.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-300">
                    <th className="text-left py-3 px-4 text-gray-700 font-semibold">
                      Event Title
                    </th>
                    <th className="text-left py-3 px-4 text-gray-700 font-semibold">
                      Date
                    </th>
                    <th className="text-left py-3 px-4 text-gray-700 font-semibold">
                      Time
                    </th>
                    <th className="text-left py-3 px-4 text-gray-700 font-semibold">
                      Location
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.upcomingEventsList.map((event, index) => (
                    <tr
                      key={index}
                      className="border-b border-gray-200 hover:bg-gray-50"
                    >
                      <td className="py-3 px-4 text-gray-800">{event.title}</td>
                      <td className="py-3 px-4 text-gray-600">
                        {new Date(event.date).toLocaleDateString("en-IN")}
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {event.time ? (
                          <>
                            <FaClock className="inline mr-1 text-orange-500" />
                            {event.time}
                          </>
                        ) : (
                          "Not specified"
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {event.location || "Not specified"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">
              No upcoming events scheduled
            </p>
          )}
        </div>

        {/* Refresh Button */}
        <div className="flex justify-end">
          <button
            onClick={fetchAllDashboardData}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg flex items-center transition"
          >
            <FaSync className="mr-2" />
            Refresh Data
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
